import { useDroppable } from '@dnd-kit/core'
import { useEffect, useCallback, useState } from 'react'

export interface UseChartDropZoneReturn {
  /** Whether this chart is currently being dragged over */
  isOver: boolean
  /** Ref to attach to the droppable element */
  setNodeRef: (node: HTMLElement | null) => void
}

export interface UseChartDragDropReturn {
  /** Current chart being dragged over (null if none) */
  dragOverChart: string | null
  /** Set the current drag over chart */
  setDragOverChart: (chartId: string | null) => void
  /** Check if a specific chart is being dragged over */
  isChartDraggedOver: (chartId: string) => boolean
}

export interface UseChartDragDropOptions {
  /** Called when drag over state changes */
  onDragOverChange?: (chartId: string | null) => void
  /** Whether drag and drop is enabled */
  enabled?: boolean
}

export interface UseChartDropZoneOptions {
  /** Chart ID for this drop zone */
  chartId: string
  /** Whether drag and drop is enabled */
  enabled?: boolean
  /** Called when drag over state changes for this chart */
  onDragOverChange?: (chartId: string | null) => void
}

/**
 * Hook for managing global chart drag-and-drop state
 * 
 * Manages drag over state across multiple charts and provides utilities
 * for checking drag state.
 * 
 * @param options - Configuration options for drag and drop
 * @returns Object with drag state management functions
 */
export function useChartDragDrop(options: UseChartDragDropOptions = {}): UseChartDragDropReturn {
  const {
    onDragOverChange,
    enabled = true
  } = options

  // Track the current chart being dragged over
  const [dragOverChart, setDragOverChartState] = useState<string | null>(null)

  /**
   * Set the current drag over chart and notify callback
   */
  const setDragOverChart = useCallback((chartId: string | null) => {
    if (!enabled) {
      return
    }

    setDragOverChartState(chartId)
    
    if (onDragOverChange) {
      onDragOverChange(chartId)
    }

    if (chartId) {
      console.log(`Chart ${chartId} is being dragged over`)
    } else {
      console.log('No chart is being dragged over')
    }
  }, [enabled, onDragOverChange])

  /**
   * Check if a specific chart is being dragged over
   */
  const isChartDraggedOver = useCallback((chartId: string): boolean => {
    return dragOverChart === chartId
  }, [dragOverChart])

  return {
    dragOverChart,
    setDragOverChart,
    isChartDraggedOver
  }
}

/**
 * Hook for creating a drop zone for a specific chart
 * 
 * Must be used within individual chart components to create droppable areas.
 * Works with @dnd-kit/core and the parent DndContext.
 * 
 * @param options - Configuration options for this drop zone
 * @returns Object with drop zone state and ref
 */
export function useChartDropZone(options: UseChartDropZoneOptions): UseChartDropZoneReturn {
  const {
    chartId,
    enabled = true,
    onDragOverChange
  } = options

  // Use @dnd-kit's useDroppable hook for this specific chart
  const { isOver, setNodeRef } = useDroppable({
    id: `chart-${chartId}`,
    data: {
      type: 'chart',
      chartId
    },
    disabled: !enabled
  })

  // Handle drag over state changes for this chart
  useEffect(() => {
    if (!enabled || !onDragOverChange) {
      return
    }

    if (isOver) {
      onDragOverChange(chartId)
    } else {
      onDragOverChange(null)
    }
  }, [isOver, chartId, enabled, onDragOverChange])

  return {
    isOver,
    setNodeRef
  }
}

export default useChartDragDrop 